import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Clock, CheckCircle2, LogOut, RefreshCw } from 'lucide-react'

export default function Setup() {
  const navigate = useNavigate()
  const [checking, setChecking] = useState(false)

  const { data: request, refetch } = useQuery({
    queryKey: ['my-institution-request'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data } = await (supabase
        .from('institution_requests' as any)
        .select('org_name, status, provisioned_org_id, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle() as any)
      return data as { org_name: string; status: string; provisioned_org_id: string | null; created_at: string } | null
    },
  })

  // If org is now provisioned, redirect to dashboard
  useEffect(() => {
    if (request?.status === 'provisioned' && request?.provisioned_org_id) {
      navigate('/dashboard', { replace: true })
    }
  }, [request])

  const handleCheckStatus = async () => {
    setChecking(true)
    await refetch()
    setChecking(false)
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    navigate('/', { replace: true })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex flex-col items-center justify-center p-6">
      {/* CONORBIT wordmark */}
      <Link to="/" className="flex items-center mb-10 transition-transform hover:-translate-y-0.5">
        <img src="/conorbit-logo-transparent.png" alt="CONORBIT" className="h-14 w-auto" />
      </Link>

      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 shadow-sm">
        {/* Status icon */}
        <div className="flex justify-center mb-6">
          {request?.status === 'provisioned' ? (
            <div className="w-16 h-16 rounded-full bg-[#E1F5EE] flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#006B3C]" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-[#FEF9E7] flex items-center justify-center">
              <Clock className="w-8 h-8 text-[#C8A84B]" />
            </div>
          )}
        </div>

        <h1 className="text-[20px] font-bold text-center mb-2">
          {request?.status === 'provisioned'
            ? 'Your institution is ready'
            : 'Your request is under review'}
        </h1>

        {request ? (
          <p className="text-justify text-[13px] text-muted-foreground text-center mb-6">
            {request.status === 'provisioned'
              ? `${request.org_name} has been set up on CONORBIT. Redirecting you now…`
              : `We received your request for ${request.org_name}. Our team is reviewing it and will configure your institution within 24–48 hours.`}
          </p>
        ) : (
          <p className="text-justify text-[13px] text-muted-foreground text-center mb-6">
            Your account has been created. Our team will review your institution request
            and contact you within 24–48 hours to get you set up.
          </p>
        )}

        {/* What happens next */}
        {request?.status !== 'provisioned' && (
          <div className="bg-muted/40 rounded-xl p-4 mb-6 space-y-3">
            <h2 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
              What happens next
            </h2>
            {[
              'Our team reviews your request (usually same day)',
              'We configure your institution, branding and team access',
              'You receive a confirmation email with your sign-in link',
              'You sign in and find your institution fully ready',
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#0D2240] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="text-[12.5px] text-muted-foreground">{step}</span>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <Button
            onClick={handleCheckStatus}
            disabled={checking}
            variant="outline"
            className="w-full"
          >
            {checking
              ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Checking…</>
              : <><RefreshCw className="w-4 h-4 mr-2" /> Check status</>}
          </Button>
          <Button
            onClick={handleSignOut}
            variant="ghost"
            className="w-full text-muted-foreground"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>

        <p className="text-justify text-center text-[11px] text-muted-foreground mt-5">
          Questions? Email{' '}
          <a href="mailto:hello@conorbit.com" className="underline hover:text-foreground">
            hello@conorbit.com
          </a>
        </p>
      </div>
    </div>
  )
}
